import * as React from "react";
import { useForm, useFieldArray, useWatch, Control } from "react-hook-form";
import {
  Button,
  Grid,
  Title,
  Text,
  Tabs,
  SegmentedControl,
  Modal,
  ActionIcon,
  Flex,
  Stack,
  Divider,
  TextInput,
  Group,
} from "@mantine/core";
import { CTextInput, CMultiSelect, CSelect, CTextarea, CRadioGroup } from "@supportinitiative/react-hook-form-mantine";
import { object, string, number, date, array, setLocale } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { getConfirmationStatus, getSystemStatus } from "../../../UseCaseUtils";
import { UseCaseCVMContext } from "../../../UseCaseCVMContext";
import { UAction, UReportActionType, UseCaseType } from "@supportinitiative/common";
import { getId } from "src/utilities";
import { IconPlus, IconTrash } from "@tabler/icons-react";

setLocale({
  mixed: {
    required: "Required",
    notType: "Invalid Value",
  },
});

const UReportActionSchema = object({
  id: string().required(),
  name: string().required(),
  type: string().required().oneOf(["REPORT"]),
  description: string().nullable(),
  definition: object({
    type: string().required().oneOf(["EXCEL", "CSV"]),
    dataPopulation: array().of(
      object({
        name: string().required(),
        genFunction: string().required(),
        id: string().required(),
      }),
    ),
  }),
});

function Report({
  recon,
  save,
  cancel,
  action,
}: {
  cancel: () => void;
  save: (values: any) => void;
  action: any;
  recon?: UseCaseType["definition"]["recons"][number];
}) {
  const { state: useCaseFormState } = React.useContext(UseCaseCVMContext);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<UAction<UReportActionType>>({
    defaultValues: {
      ...action,
      definition: {
        ...action.definition,
        type: action.definition.type || "CSV",
      },
    },
    criteriaMode: "all",
    resolver: yupResolver(UReportActionSchema),
  });

  const dataPopulation = useFieldArray({
    control,
    name: "definition.dataPopulation",
  });

  return (
    <Modal centered size="xl" opened={true} onClose={cancel} title={<Title order={4}>Report</Title>}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          // handleSubmit(onSubmitForm)();
          event.stopPropagation();
          console.log("inside child");
          // cancel();
        }}
        noValidate
      >
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={6}>
            <CTextInput
              name="name"
              control={control}
              label="Name"
              required
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <CTextInput name="id" control={control} label="ID" required showErrorText={false} disabled={true} />
          </Grid.Col>
        </Grid>

        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CTextarea
              name="description"
              minLength={5}
              control={control}
              label="Description"
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CSelect
              name={`definition.type`}
              control={control}
              label={"Type"}
              data={[
                {
                  value: "EXCEL",
                  label: "EXCEL",
                },
                {
                  value: "CSV",
                  label: "CSV",
                },
              ]}
              showErrorText={false}
              required
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
        <Divider my="xs" label="Data Population" labelPosition="center" />
        <Flex>
          <Group mb="xs" style={{ alignItems: "center" }}>
            <Text fw={500} style={{ fontSize: "14px" }}>
              Rows
            </Text>
            <ActionIcon
              size="sm"
              variant="white"
              color="gray"
              onClick={() => {
                dataPopulation.append({
                  name: "",
                  genFunction: "",
                  id: getId(),
                });
              }}
              disabled={useCaseFormState.actionOptions.disableAll}
            >
              <IconPlus />
            </ActionIcon>
          </Group>
        </Flex>
        <Stack>
          {dataPopulation.fields.map((field, index) => {
            return (
              <Grid align="stretch" gutter={"xs"} mb="xs" key={field.id}>
                <Grid.Col span={3}>
                  <CTextInput
                    name={`definition.dataPopulation.${index}.name`}
                    control={control}
                    label={"Name"}
                    disabled={useCaseFormState.actionOptions.disableAll}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <CTextarea
                    name={`definition.dataPopulation.${index}.genFunction`}
                    control={control}
                    label={"Generate Function"}
                    minRows={4}
                    disabled={useCaseFormState.actionOptions.disableAll}
                    required
                  />
                </Grid.Col>
                <Grid.Col span={2}>
                  <CTextInput
                    name={`definition.dataPopulation.${index}.id`}
                    control={control}
                    label={"Id"}
                    disabled={useCaseFormState.actionOptions.disableAll}
                  />
                </Grid.Col>
                <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
                  <ActionIcon
                    size="sm"
                    variant="white"
                    color="gray"
                    onClick={() => {
                      dataPopulation.remove(index);
                    }}
                    disabled={useCaseFormState.actionOptions.disableAll}
                  >
                    <IconTrash />
                  </ActionIcon>
                </Grid.Col>
              </Grid>
            );
          })}
        </Stack>

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={2}>
            <Button
              color="teal"
              variant="filled"
              fullWidth
              type="submit"
              onClick={
                useCaseFormState.actionOptions.disableAll
                  ? () => {
                      console.log("only cancel");
                      cancel();
                    }
                  : async (event: React.MouseEvent<HTMLButtonElement>) => {
                      event.preventDefault();
                      const values = getValues();
                      // console.log("values", values);
                      clearErrors();
                      try {
                        await UReportActionSchema.validate(values, {
                          abortEarly: false,
                        });

                        console.log("values", values);
                        save?.(values);
                        //cancel();
                      } catch (error) {
                        console.log("errors", error);
                        console.log("errors", JSON.stringify(error));
                        console.log("error inner", JSON.stringify(error));
                        (error as any).inner.forEach((err: any) => {
                          setError(err.path, {
                            type: "custom",
                            message: err.message,
                          });
                        });
                      }
                    }
              }
            >
              Ok
            </Button>
          </Grid.Col>
        </Grid>
      </form>
    </Modal>
  );
}

export default Report;
